import { ChartModel, MarketBar } from './chart-model';
import { referenceMatchesChart } from './chart-levels';
import { ChartSelection, clientToSvgX, selectionAtX } from './chart-interaction';
import {
    formatBarTime,
    formatPercent,
    formatPrice,
    formatVolume
} from './chart-format';

export type InteractionMode = 'mouse' | 'touch' | 'keyboard' | null;
export type TooltipPosition = { left: number; top: number };

const TOOLTIP_WIDTH = 218;
const TOOLTIP_HEIGHT = 126;
const TOOLTIP_GAP = 14;
const TOOLTIP_MARGIN = 10;

export function displayChangePercentage(
    selection: ChartSelection | null,
    market: any,
    bar: MarketBar | null
) {
    const previousClose = Number(market?.previous_close || 0);
    const close = Number(bar?.close || 0);
    if (
        !selection
        || !referenceMatchesChart(market)
        || previousClose <= 0
        || close <= 0
    ) return null;
    return (close / previousClose - 1) * 100;
}

export function pointerContext(
    model: ChartModel,
    event: PointerEvent
): { selection: ChartSelection | null; tooltipPosition: TooltipPosition } | null {
    const svg = event.currentTarget as SVGSVGElement;
    const rect = svg?.getBoundingClientRect();
    if (!rect || rect.width <= 0 || rect.height <= 0) return null;

    const pointerX = Math.max(0, Math.min(event.clientX - rect.left, rect.width));
    const pointerY = Math.max(0, Math.min(event.clientY - rect.top, rect.height));
    const width = Math.max(
        0,
        Math.min(TOOLTIP_WIDTH, rect.width - TOOLTIP_MARGIN * 2)
    );
    const maxLeft = Math.max(TOOLTIP_MARGIN, rect.width - width - TOOLTIP_MARGIN);
    const preferredLeft = pointerX + TOOLTIP_GAP + width <= rect.width - TOOLTIP_MARGIN
        ? pointerX + TOOLTIP_GAP
        : pointerX - TOOLTIP_GAP - width;
    const preferredTop = pointerY + TOOLTIP_GAP + TOOLTIP_HEIGHT
        <= rect.height - TOOLTIP_MARGIN
        ? pointerY + TOOLTIP_GAP
        : pointerY - TOOLTIP_GAP - TOOLTIP_HEIGHT;

    return {
        selection: selectionAtX(
            model,
            clientToSvgX(event.clientX, rect.left, rect.width)
        ),
        tooltipPosition: {
            left: Math.max(TOOLTIP_MARGIN, Math.min(preferredLeft, maxLeft)),
            top: Math.max(
                TOOLTIP_MARGIN,
                Math.min(preferredTop, rect.height - TOOLTIP_HEIGHT - TOOLTIP_MARGIN)
            )
        }
    };
}

export function emptyChartText(hasTarget: boolean, session: string, market: any) {
    if (!hasTarget) return '종목을 선택하면 차트가 표시됩니다.';
    const current = String(session || '').toUpperCase();
    if (['PREMARKET', 'PRE_MARKET'].includes(current)) {
        return '장 시작 후 확정 1분봉이 표시됩니다.';
    }
    if (
        ['HOLIDAY', 'CLOSED', 'AFTER_HOURS', 'POST_MARKET', 'POSTMARKET']
            .includes(current)
    ) return '표시할 최근 확정 1분봉이 없습니다.';
    if (market?.delayed) return '차트 업데이트를 확인하고 있습니다.';
    return '첫 확정 1분봉을 기다리고 있습니다.';
}

export function chartAriaValueText(bar: MarketBar | null, change: number | null) {
    if (!bar) return '';
    return (
        `${formatBarTime(bar.at)}, 시가 ${formatPrice(bar.open)}, `
        + `고가 ${formatPrice(bar.high)}, 저가 ${formatPrice(bar.low)}, `
        + `종가 ${formatPrice(bar.close)}, 거래량 ${formatVolume(bar.volume)}`
        + (change === null ? '' : `, 전일 종가 대비 ${formatPercent(change)}`)
    );
}

export function chartSummary(
    model: ChartModel,
    selection: ChartSelection | null,
    market: any,
    fallback: string
) {
    const bar = selection?.candle || model.latest;
    if (!bar) return fallback;
    const source = model.currentPriceRealtime ? '실시간 체결가' : '최근 확정가';
    const context = model.previousTradingDate ? '이전 거래일 차트. ' : '';
    const selected = selection ? '선택한 봉. ' : '';
    return (
        `${context}${selected}${market?.trading_date || ''} 확정 1분봉 `
        + `${model.bars.length}개. 기준 ${formatBarTime(bar.at)}. `
        + `시가 ${formatPrice(bar.open)}, 고가 ${formatPrice(bar.high)}, `
        + `저가 ${formatPrice(bar.low)}, 종가 ${formatPrice(bar.close)}. `
        + `${source} ${formatPrice(model.currentPrice)}.`
    );
}
